<script setup lang="ts">
import { computed, ref } from "vue";
import Title from "@/components/Title.vue";
import { ScaleIcon } from '@heroicons/vue/24/outline';
import Vue3Slider from "vue3-slider"
import Divider from "@/components/Divider.vue";
import Content from "@/components/Content.vue";

const growth = ref(15);
const profitability = ref(25);
const ruleOf40 = computed(() => growth.value + profitability.value);
</script>

<template>
  <Title subtitle="Resources">Rule of 40</Title>
  <Divider/>
  <Content>
    <p class="pb-8">
      The "Rule of 40" is a financial metric used in the technology industry, particularly among SaaS companies and
      startups, to assess the balance between a company's growth and profitability. It's main use is to to evaluate
      the overall health and sustainability of a company.
    </p>

    <div class="sm:mx-40 p-2 text-green-800 border-t-4 border-green-300 bg-green-50 dark:text-green-400 dark:bg-gray-700 dark:border-green-800 text-lg font-medium">
      The "Rule of 40" suggests that if the sum of the revenue growth rate and the EBITDA margin percentage is equal to or greater than 40,
      the company is in a healthy financial position.
    </div>

    <div class="grid gap-2 sm:grid-cols-3 py-12">
      <div class="flex flex-col items-center">
        <h5 class="text-3xl font-bold">Growth</h5>
        <p class="mb-5">Revenue Growthe Rate</p>

        <p class="mb-1 text-xs">Revenue Growth</p>
        <div class="flex items-center pl-16">
          <Vue3Slider v-model="growth" width="200px" height="10" :step="5" :min="-40" :max="40" orientation="vertical" color="#333" track-color="#ddd"/>
          <div class="ml-2 w-16 text-lg">{{ growth }}%</div>
        </div>
        <p class="mt-1 text-xs">Revenue Decline</p>
      </div>

      <div class="flex flex-col items-center justify-center">
        <ScaleIcon class="block w-20 h-20 mb-2"/>

        <div :class="[
            'flex justify-center items-center w-24 h-24 rounded-full text-4xl text-white dark:text-primary',
            ruleOf40 >= 40 ? 'bg-green-500' : 'bg-red-500',
          ]">
          {{ ruleOf40 }}%
        </div>
      </div>

      <div class="flex flex-col items-center">
        <h5 class="text-3xl font-bold">Profitability</h5>
        <p class="mb-5">EBITDA Margin Percentage</p>

        <p class="mb-1 text-xs">Profits</p>
        <div class="flex items-center pr-16">
          <div class="mr-2 w-16 text-lg text-right">{{ profitability }}%</div>
          <Vue3Slider v-model="profitability" width="200px" height="10" :step="5" :min="-40" :max="40" tooltip orientation="vertical" color="#333" track-color="#ddd"/>
        </div>
        <p class="mt-1 text-xs">Losses</p>
      </div>
    </div>

    <div class="grid gap-2 sm:grid-cols-3">
      <div class="text-center text-sm">
        <p class="mb-2">
          <b>Revenue Growth</b> is simply the difference in revenue between two
          points in time. The percentage of this change is the <b>Revenue Growth Rate</b>.
        </p>
        <p>
          Last year's revenue: $2,000,000<br>
          This year's revenue: $2,500,000<br>
          Revenue Growth: $500,000<br>
          Revenue Growth Rate: 25%
        </p>
      </div>
      <div></div>
      <div class="text-center text-sm">
        <p class="mb-2">
          <b>EBITDA</b> is Earnings Before Interest, Taxes, Depreciation, and Amortization.
          It is a rough proxy for the cash flow generated by a company. The <b>EBITDA Margin</b>
          is calculated by comparing the EBITDA to the revenue.
        </p>
        <p>
          EBITDA: $500,000<br>
          Revenue: $2,500,000<br>
          EBITDA Margin: 20%
        </p>
      </div>
    </div>
  </Content>
</template>

